import React, { useCallback, useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import useQueryGetUserChart, { IFallChartItem } from '@/api/user/getUserChart.ts'
import { format } from 'date-fns'
import useQueryGetUserDetail from '@/api/auth/getUser.ts'
import AverageStat from '@/pages/users/AverageStat.tsx'
import useQueryGetUserStatAverage from '@/api/user/getUserStatAverage.ts'
import { useTranslation } from 'react-i18next'
import { useViewMode } from '@/pages/users/detail/DailyChart.tsx'

export interface IProps {
    className?: string
    id: number
}

const FallChartContainer: React.FC<IProps> = ({ className, id }) => {
    const { t } = useTranslation()
    const { viewMode, setViewMode } = useViewMode();

    const { data: detail } = useQueryGetUserDetail(id)
    const { data: averageStats } = useQueryGetUserStatAverage(id || -1)
    const { data } = useQueryGetUserChart(id || -1)

    // 원본 데이터에 타임스탬프 추가
    const originalItems = useMemo(() => {
        const items = data?.fall?.map((item: IFallChartItem) => ({
            x: new Date(item.x).getTime(), // 문자열 → timestamp 변환
            y: item.y || 0
        })) || []
        
        // 날짜 기준으로 오름차순 정렬 (과거 -> 최신)
        return items.sort((a: { x: number }, b: { x: number }) => a.x - b.x)
    }, [data])

    // 뷰 모드에 따라 데이터 필터링
    const filteredData = useMemo(() => {
        if (!originalItems || originalItems.length === 0) return []
        
        // 필터링 제거: 전체 데이터 반환
        return originalItems;
    }, [originalItems, viewMode])

    // 뷰 모드에 따라 데이터 그룹화
    const groupedData = useMemo(() => {
        if (filteredData.length === 0) return [];
        
        const groups: {[key: string]: number} = {};
        
        filteredData.forEach(item => {
            const date = new Date(item.x);
            let key = '';
            
            if (viewMode === 'yearly') {
                // 연도별로 그룹화
                key = `${date.getFullYear()}`;
            } else if (viewMode === 'monthly') {
                // 월별로 그룹화
                key = `${date.getFullYear()}-${date.getMonth()}`;
            } else {
                // 일별로 그룹화 (이미 일별 데이터)
                key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
            }
            
            // 그룹에 값 누적
            if (groups[key]) {
                groups[key] += item.y;
            } else {
                groups[key] = item.y;
            }
        });
        
        // 그룹화된 데이터를 다시 배열로 변환
        const result = Object.entries(groups).map(([key, sum]) => {
            let timestamp: number;
            
            if (viewMode === 'yearly') {
                // 연도의 중간점(7월)을 타임스탬프로 사용
                timestamp = new Date(parseInt(key), 6, 1).getTime();
            } else if (viewMode === 'monthly') {
                // key = "2023-5" 형태
                const [year, month] = key.split('-').map(Number);
                // 월의 중간일(15일)을 타임스탬프로 사용
                timestamp = new Date(year, month, 15).getTime();
            } else {
                // key = "2023-5-20" 형태
                const [year, month, day] = key.split('-').map(Number);
                timestamp = new Date(year, month, day).getTime();
            }
            
            return { x: timestamp, y: sum };
        });
        
        return result.sort((a, b) => a.x - b.x);
    }, [filteredData, viewMode]);

    // 차트의 Y축 최대값 계산
    const maxYValue = useMemo(() => {
        if (!groupedData || groupedData.length === 0) return 2;
        const max = Math.max(...groupedData.map(item => item.y || 0));
        // 최대값에 따라 스케일 조정 (상대적인 표시)
        return max <= 1 ? 2 : (max + 1);
    }, [groupedData]);

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            try {
                const x = payload[0].payload.x
                const y = payload[0].payload.y
                
                if (!x || isNaN(x)) {
                    return null;
                }

                return (
                    <div className={`min-w-[83px] p-[10px] bg-white bg-opacity-100 rounded-lg shadow border border-solid border-main_b`}>
                        <p className={'text-[12px] text-black text-center'}>
                            {formatChartXAxis(x)}
                        </p>
                        <p className={`text-[16px] text-center font-bold text-main_b`}>{Math.floor(y).toFixed(0)} {t('common.count')}</p>
                    </div>
                )
            } catch (error) {
                return null;
            }
        }
        return null
    }

    const formatChartXAxis = useCallback((x: any) => {
        if (!x || isNaN(x)) return '';
        
        try {
            switch (viewMode) {
                case 'yearly':
                    return format(x, t('users.dailyChart.format.yearly')); // 년도만 표시
                case 'monthly':
                    return format(x, t('users.dailyChart.format.monthly')); // 년도와 월 표시
                case 'daily':
                    return format(x, t('users.dailyChart.format.daily')); // 월과 일 표시
                default:
                    return format(x, t('common.date.format.monthDay'));
            }
        } catch (error) {
            return '';
        }
    }, [t, viewMode])

    // X축 틱 생성
    const chartTicks = useMemo(() => {
        let ticks: number[] = [];
        
        if (!groupedData?.length) return ticks;
        
        // 적절한 간격으로 틱 선택
        if (viewMode === 'yearly') {
            // 모든 연도 표시
            groupedData.forEach(item => {
                ticks.push(item.x);
            });
        } else if (viewMode === 'monthly') {
            // 3개월 간격으로 표시
            const uniqueMonths: { [key: string]: number } = {};
            
            groupedData.forEach(item => {
                const date = new Date(item.x);
                const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
                uniqueMonths[monthKey] = item.x;
            });
            
            ticks = Object.values(uniqueMonths);
            
            // 데이터가 많으면 3개월 간격으로 표시
            if (ticks.length > 12) {
                ticks = ticks.filter((_, index) => index % 3 === 0);
            }
        } else {
            // 5일 간격으로 표시
            const uniqueDays: { [key: string]: number } = {};
            
            groupedData.forEach(item => {
                const date = new Date(item.x);
                const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
                uniqueDays[dayKey] = item.x;
            });
            
            ticks = Object.values(uniqueDays);
            
            // 데이터가 많으면 5일 간격으로 표시
            if (ticks.length > 10) {
                ticks = ticks.filter((_, index) => index % 5 === 0);
            }
        }
        
        return ticks.sort((a, b) => a - b);
    }, [groupedData, viewMode]);

    return (
        <div className={`${className} flex flex-col py-[40px] px-[34px]`}>
            {/* 뷰 모드 선택 버튼 - 상단에 배치 */}
            <div className="mb-4 px-3 py-2 bg-gray-100 rounded-md text-sm flex items-center">
                <span className="font-medium mr-4">
                    {viewMode === 'yearly' ? t('common.yearView') : 
                     viewMode === 'monthly' ? t('common.monthView') : 
                     t('common.dayView')}
                </span>
                <div className="flex space-x-2">
                    <button 
                        className={`px-3 py-1 text-xs rounded ${viewMode === 'yearly' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                        onClick={() => setViewMode('yearly')}
                    >
                        {t('common.yearView')}
                    </button>
                    <button 
                        className={`px-3 py-1 text-xs rounded ${viewMode === 'monthly' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                        onClick={() => setViewMode('monthly')}
                    >
                        {t('common.monthView')}
                    </button>
                    <button 
                        className={`px-3 py-1 text-xs rounded ${viewMode === 'daily' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                        onClick={() => setViewMode('daily')}
                    >
                        {t('common.dayView')}
                    </button>
                </div>
            </div>

            <div className="flex gap-[3px]">
                {/* 왼쪽: 평균 정보 - 걸음수 형태와 같은 스타일 적용 */}
                <div className={'flex flex-col gap-[20px] justify-center'}>
                    <AverageStat title={t('users.detail.falldetection.average.daily')}
                                desc={averageStats?.fall?.day?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                postfix={t('common.count')} />
                    <AverageStat title={t('users.detail.falldetection.average.weekly')}
                                desc={averageStats?.fall?.week?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                postfix={t('common.count')} />
                    <AverageStat title={t('users.detail.falldetection.average.monthly')}
                                desc={averageStats?.fall?.month?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                postfix={t('common.count')} />
                    <AverageStat title={t('users.detail.falldetection.average.yearly')}
                                desc={averageStats?.fall?.year?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                postfix={t('common.count')} />
                </div>
                
                {/* 오른쪽: 그래프 */}
                {detail?.device && originalItems.length > 0 ? (
                    <div className="flex-1">
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart 
                                data={groupedData} 
                                margin={{ top: 5, left: 0, right: 8, bottom: 40 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="x" 
                                    tickFormatter={formatChartXAxis} 
                                    ticks={chartTicks}
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                    dy={20}
                                    tick={{ fontSize: 11 }}
                                    label={{ 
                                        value: viewMode === 'yearly' 
                                            ? t('users.dailyChart.label.yearly') 
                                            : viewMode === 'monthly' 
                                            ? t('users.dailyChart.label.monthly') 
                                            : t('users.dailyChart.label.daily'),
                                        position: 'insideBottom', 
                                        dy: 15,
                                        style: { fill: '#666', fontSize: 12 } 
                                    }}
                                />
                                <YAxis 
                                    domain={[0, maxYValue]} 
                                    ticks={Array.from({length: maxYValue + 1}, (_, i) => i)}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="y" fill="#FF4545" barSize={viewMode === 'yearly' ? 40 : viewMode === 'monthly' ? 30 : 20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <div className={'h-[300px] flex-1 flex items-center justify-center'}>
                        <p className={'text-body'}>
                            {detail?.device && originalItems.length === 0 
                                ? t('users.detail.falldetection.empty') 
                                : t('users.detail.falldetection.empty.connectDevice')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}

export default FallChartContainer 
